import { useEffect, useMemo, useState } from "react"
import { Box, Fade, Grid, SelectChangeEvent, Stack } from "@mui/material"
import { Form, Formik } from "formik"
import { makeStyles } from "tss-react/mui"
import type * as Yup from "yup"

import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import {
    BaseFormValues,
    BillReceiversFormValuesType,
    BillReceiversType,
    BillTypeMap,
    CountryCode,
    PrivateCompanyDetailsFormValues,
    PrivatePersonDetailsFormValues,
    PublicInstitutionDetailsFormValues,
    SendInvoiceCompany,
} from "../billReceiverTypes"
import { BillReceiverType } from "../../../../modules/company/admin/organization/userGroups/userGroupsTypes"

import { StandardDialog } from "../../dialogs/StandardDialog"
import {
    createBillReceiverDetailsSchemas,
    getBillReceiverSearchValidationError,
    getSendInvoiceCompanyValidationError,
} from "../validation/billReceiverValidation"
import type { CreateBillReceiverDialogCoreProps } from "./dialogTypes"

import { DetailsCountrySelect } from "../details-form/DetailsCountrySelect"
import { DynamicForm } from "../details-form/DynamicForm"
import { SearchInvoiceSection } from "../details-form/SearchInvoiceSection"
import { SendInvoiceCompanySection } from "../details-form/SendInvoiceCompanySection"
import { CreateBillTypeSelect } from "../details-form/CreateBillTypeSelect"
import { StandardLoadingButton } from "../../buttons/LoadingButton"

type FormConfig = Readonly<{
    initialValues: BillReceiversFormValuesType
    validationSchema: Yup.AnyObjectSchema
}>

const useStyles = makeStyles()(() => ({
    dialogPaper: {
        position: "relative",
    },
    form: {
        padding: "0rem 10rem 3rem",
    },
}))

const fadeConfig = {
    timeout: 700,
    in: true,
}

const baseInitialValues: BaseFormValues = {
    country: CountryCode.DA,
    debtorNumber: "",
    addressLine1: "",
    addressLine2: "",
    postalCode: "",
    city: "",
    telephone: "",
}

const privatePersonInitialValues: PrivatePersonDetailsFormValues = {
    ...baseInitialValues,
    name: "",
}

const publicInstitutionInitialValues: PublicInstitutionDetailsFormValues = {
    ...baseInitialValues,
    institutionName: "",
    email: "",
    contactPerson: "",
    vatnumber: "",
}

const privateCompanyInitialValues: PrivateCompanyDetailsFormValues = {
    ...baseInitialValues,
    companyName: "",
    email: "",
    contactPerson: "",
}

const defaultSendInvoiceCompany: SendInvoiceCompany = {
    invoiceType: BillReceiverType.EMAIL_ADDRESS,
    search: "",
}

export function CreateBillReceiverDialogCore<TBillReceiverItem>({
    open,
    onClose,
    title,
    disableBackdropClick,
    onSimpleSelect,
    onSelectBillReceiver,
    onSearch,
    isSearching,
    renderAddBillReceiverDialog,
    closeOnSubmit,
}: CreateBillReceiverDialogCoreProps<TBillReceiverItem>) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const [billType, setBillType] = useState<BillReceiversType | null>(null)
    const [search, setSearch] = useState("")
    const [billReceiverExists, setBillReceiverExists] = useState<boolean>()
    const [showForm, setShowForm] = useState(false)
    const [addBillReceiverModalOpen, setAddBillReceiverModalOpen] = useState(false)
    const [createNew, setCreateNew] = useState(false)
    const [companySendInvoiceTo, setCompanySendInvoiceTo] = useState<SendInvoiceCompany>(defaultSendInvoiceCompany)

    const { privatePerson, publicInstitution, privateCompany } = useMemo(
        () => createBillReceiverDetailsSchemas(l10n),
        [l10n],
    )

    const formConfig: BillTypeMap<FormConfig> = useMemo(
        () => ({
            [BillReceiversType.PRIVATE_PERSON]: {
                initialValues: privatePersonInitialValues,
                validationSchema: privatePerson,
            },
            [BillReceiversType.PUBLIC_INSTITUTION]: {
                initialValues: publicInstitutionInitialValues,
                validationSchema: publicInstitution,
            },
            [BillReceiversType.PRIVATE_COMPANY]: {
                initialValues: privateCompanyInitialValues,
                validationSchema: privateCompany,
            },
        }),
        [privatePerson, publicInstitution, privateCompany],
    )

    const handleBillTypeChange = (event: SelectChangeEvent<BillReceiversType>) => {
        const value = event.target.value as BillReceiversType
        setBillType(value)
        setSearch("")
        setCompanySendInvoiceTo(defaultSendInvoiceCompany)
        setShowForm(false)
        setBillReceiverExists(undefined)
        setCreateNew(false)
        setAddBillReceiverModalOpen(false)
    }

    const searchValidationError = useMemo(
        () => getBillReceiverSearchValidationError({ billType, search, l10n }),
        [billType, l10n, search],
    )

    const onSearchClick = async () => {
        if (searchValidationError) {
            return
        }
        const searchRes = await onSearch({ search })
        setBillReceiverExists(searchRes.exists)

        if (searchRes.exists) {
            setAddBillReceiverModalOpen(true)
        }

        if (billType !== BillReceiversType.PRIVATE_COMPANY) {
            setShowForm(true)
        }
    }

    const shouldShowPrivateCompanyFields =
        billType === BillReceiversType.PRIVATE_COMPANY &&
        (createNew || !billReceiverExists) &&
        billReceiverExists !== undefined

    useEffect(() => {
        if (shouldShowPrivateCompanyFields) {
            setShowForm(true)
        }
    }, [shouldShowPrivateCompanyFields])

    const submitForm = async (value: BillReceiversFormValuesType) => {
        await onSelectBillReceiver({
            ...value,
            type: billType,
            search,
            companySendInvoice: companySendInvoiceTo.search.trim() ? companySendInvoiceTo : undefined,
        })

        if (closeOnSubmit) {
            onClose()
        }
    }

    const onReceiverSelect = (receiver: TBillReceiverItem) => {
        onSimpleSelect && onSimpleSelect(receiver)
        onClose()
    }

    const sendInvoiceCompanyValidationError = useMemo(
        () =>
            getSendInvoiceCompanyValidationError({
                invoiceType: companySendInvoiceTo.invoiceType,
                search: companySendInvoiceTo.search,
                l10n,
            }),
        [companySendInvoiceTo.invoiceType, companySendInvoiceTo.search, l10n],
    )

    const isContinueDisabled = Boolean(searchValidationError) || isSearching
    const shouldDisableCreateButton =
        shouldShowPrivateCompanyFields &&
        (!companySendInvoiceTo.search.trim() || Boolean(sendInvoiceCompanyValidationError))

    return (
        <StandardDialog
            open={open}
            scroll="body"
            onClose={onClose}
            disableBackdropClick={disableBackdropClick}
            maxWidthProp="md"
            dialogTitle={title}
            slotProps={{ paper: { className: classes.dialogPaper } }}
        >
            <Formik<BillReceiversFormValuesType>
                enableReinitialize
                initialValues={billType ? formConfig[billType].initialValues : formConfig.PRIVATE_PERSON.initialValues}
                validationSchema={
                    billType ? formConfig[billType].validationSchema : formConfig.PRIVATE_PERSON.validationSchema
                }
                onSubmit={submitForm}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <Stack direction="column" spacing="1rem" className={classes.form}>
                            <DetailsCountrySelect />
                            <CreateBillTypeSelect selectedValue={billType} handleChange={handleBillTypeChange} />
                            {billType && (
                                <Fade {...fadeConfig}>
                                    <Box>
                                        <SearchInvoiceSection
                                            search={search}
                                            setSearch={setSearch}
                                            onSearchCall={onSearchClick}
                                            billType={billType}
                                            isDisabled={isSearching}
                                            validationError={searchValidationError}
                                            isContinueDisabled={isContinueDisabled}
                                        />
                                    </Box>
                                </Fade>
                            )}

                            {billType === BillReceiversType.PRIVATE_COMPANY &&
                                (createNew || !billReceiverExists) &&
                                billReceiverExists !== undefined && (
                                    <Fade {...fadeConfig}>
                                        <Box>
                                            <SendInvoiceCompanySection
                                                value={companySendInvoiceTo}
                                                onChange={setCompanySendInvoiceTo}
                                                validationError={sendInvoiceCompanyValidationError}
                                            />
                                        </Box>
                                    </Fade>
                                )}

                            {billType && showForm && (createNew || !billReceiverExists) && (
                                <Fade {...fadeConfig}>
                                    <Grid container direction="column" wrap="nowrap">
                                        <Stack spacing="1rem">
                                            <DynamicForm componentType={billType} />
                                        </Stack>

                                        <Box mt="2rem" display="flex" justifyContent="center">
                                            <LocalizedStrict id="bill-receivers-create-dialog-button-create">
                                                <StandardLoadingButton
                                                    fullWidth
                                                    variant="contained"
                                                    type="submit"
                                                    disabled={shouldDisableCreateButton || isSubmitting}
                                                    loading={isSubmitting}
                                                    defaultBorderRadius
                                                >
                                                    Create
                                                </StandardLoadingButton>
                                            </LocalizedStrict>
                                        </Box>
                                    </Grid>
                                </Fade>
                            )}
                        </Stack>
                    </Form>
                )}
            </Formik>

            {addBillReceiverModalOpen
                ? renderAddBillReceiverDialog({
                      open: addBillReceiverModalOpen,
                      setOpen: setAddBillReceiverModalOpen,
                      type: "update",
                      searchText: search,
                      onChoose: onReceiverSelect,
                      onCreateNew: () => {
                          setAddBillReceiverModalOpen(false)
                          setCreateNew(true)
                      },
                  })
                : null}
        </StandardDialog>
    )
}
